import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../../services/api';
import toastService from '../../services/toast';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert('كلمة المرور غير متطابقة');
      return;
    }

    setLoading(true);
    try {
      // إضافة role_id=4 (متدرب) للمستخدمين الجدد
      const userData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        role_id: 4 // تعيين الدور لمتدرب
      };
      
      // Registration data being sent to server
      
      // استخدام الطريقة الصحيحة للـ API
      // Calling api.auth.register with data
      const response = await api.auth.register(userData);

      if (response.data) {
        toastService.success('تم إنشاء الحساب بنجاح');
        navigate('/login');
      }
    } catch (error) {
      // Error details
      // Error response data
      
      // إظهار تفاصيل الخطأ بوضوح
      if (error.response?.data?.errors) {
        // تجميع كل رسائل الخطأ في رسالة واحدة
        const errorMessages = Object.values(error.response.data.errors).flat();
        toastService.error(
          <div className="text-right" dir="rtl">
            <p className="font-bold mb-2">حدث خطأ:</p>
            <ul className="list-disc mr-5">
              {errorMessages.map((err, index) => (
                <li key={index} className="text-right">{err}</li>
              ))}
            </ul>
          </div>
        );
      } else {
        // إذا كان الخطأ بسيط
        toastService.error(error.response?.data?.message || 'حدث خطأ أثناء إنشاء الحساب');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen grid place-items-center overflow-hidden bg-white dark:bg-gray-900">
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none section-bg">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 dark:bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-[0.5rem] [border-top-right-radius:3rem] [border-bottom-left-radius:3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] dark:shadow-2xl border border-gray-300 dark:border-gray-700"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              حساب جديد
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              سجل حساب جديد للوصول لجميع المميزات
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="relative">
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="أدخل اسمك الكامل"
                  value={formData.name}
                  onChange={handleChange}
                  dir="rtl"
                />
                <label 
                  htmlFor="name" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  الاسم الكامل
                </label>
              </div>
              <div className="relative">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="أدخل رقم الجوال"
                  value={formData.phone}
                  onChange={handleChange}
                  dir="rtl"
                />
                <label 
                  htmlFor="phone" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  رقم الجوال
                </label>
              </div>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="أدخل البريد الإلكتروني"
                  value={formData.email}
                  onChange={handleChange}
                  dir="rtl"
                />
                <label 
                  htmlFor="email" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  البريد الإلكتروني
                </label>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="أدخل كلمة المرور"
                  value={formData.password}
                  onChange={handleChange}
                  dir="rtl"
                />
                <label 
                  htmlFor="password" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  كلمة المرور
                </label>
              </div>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                  placeholder="تأكيد كلمة المرور"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  dir="rtl"
                />
                <label 
                  htmlFor="confirmPassword" 
                  className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                >
                  تأكيد كلمة المرور
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <Link
                to="/login"
                className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
              >
                لديك حساب بالفعل؟
              </Link>

              <button
                type="submit"
                disabled={loading}
                className="group relative flex justify-center py-2 sm:py-2 py-3 px-6 border border-transparent text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                {loading ? (
                  <div className="w-4 sm:w-4 w-5 h-4 sm:h-4 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "إنشاء حساب"
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default Register;
